import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Facebook, Instagram, Linkedin, Youtube, CheckCircle, XCircle,
  RefreshCw, Send, Eye, Edit, Loader2, Clock, FileText,
  BarChart3, Search, Mail, Megaphone, ImageIcon, Upload,
} from 'lucide-react';
import SEO from '@/components/SEO';

const SUPABASE_URL = 'https://plizcqmxjmtzdgmmhtxj.supabase.co';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SocialPost {
  id: string;
  marketing_output_id: string | null;
  platform: string;
  content: string;
  image_prompt: string | null;
  image_url: string | null;
  status: string;
  published_at: string | null;
  created_at: string;
}

interface MarketingOutput {
  id: string;
  task_id: string;
  agent_name: string;
  agent_role: string;
  title: string;
  content: string;
  output_type: string;
  status: string;
  review_notes: string | null;
  created_at: string;
  metadata: {
    processed?: boolean;
    processed_at?: string;
    blog_post_id?: string;
    blog_post_slug?: string;
    social_post_ids?: string[];
    pipeline?: string;
    last_error?: string;
    last_error_at?: string;
  } | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLATFORM_META: Record<string, { icon: typeof Facebook; label: string; color: string }> = {
  facebook:           { icon: Facebook,  label: 'Facebook',                      color: 'bg-blue-100 text-blue-700' },
  instagram:          { icon: Instagram, label: 'Instagram',                     color: 'bg-pink-100 text-pink-700' },
  linkedin:           { icon: Linkedin,  label: 'LinkedIn, Brian (Personal)',   color: 'bg-amber-100 text-amber-800' },
  linkedin_personal:  { icon: Linkedin,  label: 'LinkedIn, Brian (Personal)',   color: 'bg-amber-100 text-amber-800' },
  linkedin_company:   { icon: Linkedin,  label: 'LinkedIn, SeniorThrive (Co)', color: 'bg-sky-100 text-sky-700' },
  youtube:            { icon: Youtube,   label: 'YouTube',                       color: 'bg-red-100 text-red-700' },
};

const STATUS_STYLES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  awaiting_image: { variant: 'outline',     label: 'Awaiting Image' },
  queued:         { variant: 'outline',     label: 'Queued' },
  pending:        { variant: 'outline',     label: 'Pending' },
  approved:       { variant: 'default',     label: 'Approved' },
  processed:      { variant: 'secondary',   label: 'Processed' },
  published:      { variant: 'secondary',   label: 'Published' },
  rejected:       { variant: 'destructive', label: 'Rejected' },
  failed:         { variant: 'destructive', label: 'Failed' },
};

const AGENT_ICONS: Record<string, typeof BarChart3> = {
  Dana:  BarChart3,
  Casey: FileText,
  Alex:  Search,
  Riley: Mail,
  Pat:   Megaphone,
};

const AGENT_COLORS: Record<string, string> = {
  Dana:  'bg-emerald-100 text-emerald-800',
  Casey: 'bg-blue-100 text-blue-800',
  Alex:  'bg-purple-100 text-purple-800',
  Riley: 'bg-amber-100 text-amber-800',
  Pat:   'bg-rose-100 text-rose-800',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByDate<T extends { created_at: string }>(items: T[]): Record<string, T[]> {
  const groups: Record<string, T[]> = {};
  for (const item of items) {
    const date = new Date(item.created_at).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(item);
  }
  return groups;
}

const PERSONAL_PLATFORMS = new Set(['linkedin', 'linkedin_personal']);

/** Group posts into topic bundles: same image_url OR same marketing_output_id.
 *  Personal LinkedIn posts are never bundled, they stay as solo cards. */
function groupIntoTopics(posts: SocialPost[]): SocialPost[][] {
  const personal: SocialPost[] = [];
  const bundleable: SocialPost[] = [];

  for (const p of posts) {
    if (PERSONAL_PLATFORMS.has(p.platform)) {
      personal.push(p);
    } else {
      bundleable.push(p);
    }
  }

  // Group bundleable posts by shared key
  const keyMap = new Map<string, SocialPost[]>();
  for (const p of bundleable) {
    const key =
      (p.marketing_output_id) ||
      (p.image_url) ||
      `solo-${p.id}`;
    if (!keyMap.has(key)) keyMap.set(key, []);
    keyMap.get(key)!.push(p);
  }

  const groups: SocialPost[][] = [];
  for (const group of keyMap.values()) groups.push(group);
  for (const p of personal) groups.push([p]); // each personal post is its own group
  return groups;
}

// ---------------------------------------------------------------------------
// Grouped Post Card, one card per topic, platform tabs inside
// ---------------------------------------------------------------------------

function GroupedPostCard({
  posts,
  onApproveAll,
  onRejectAll,
  onSaveEdit,
  onGenerateImage,
  onUploadImage,
}: {
  posts: SocialPost[];
  onApproveAll: (ids: string[]) => void;
  onRejectAll: (ids: string[]) => void;
  onSaveEdit: (id: string, content: string) => void;
  onGenerateImage?: (id: string) => void;
  onUploadImage?: (id: string, file: File) => void;
}) {
  const [activePlatform, setActivePlatform] = useState(posts[0]?.platform || 'facebook');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activePost = posts.find(p => p.platform === activePlatform) || posts[0];
  const isSolo = posts.length === 1;
  const imagePost = posts.find(p => p.image_url) || activePost;
  const allIds = posts.map(p => p.id);
  const anyActionable = posts.some(p => !['published', 'rejected', 'approved'].includes(p.status));

  useEffect(() => {
    setDraft(activePost.content);
    setEditing(false);
  }, [activePost.id, activePost.content]);

  const activeMeta = PLATFORM_META[activePost.platform] || PLATFORM_META.facebook;
  const ActiveIcon = activeMeta.icon;

  return (
    <Card className="border border-border/60 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isSolo ? (
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${activeMeta.color}`}>
                <ActiveIcon className="h-3.5 w-3.5" />
                {activeMeta.label}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
                {posts.length} platforms
              </span>
            )}
            <Badge variant={(STATUS_STYLES[activePost.status] || STATUS_STYLES.pending).variant}>
              {(STATUS_STYLES[activePost.status] || STATUS_STYLES.pending).label}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {new Date(activePost.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Hidden file input for image upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && onUploadImage) onUploadImage(activePost.id, file);
            e.target.value = '';
          }}
        />

        {/* Media preview, shared across platforms (video or image) */}
        {imagePost.image_url && (
          <div className="flex flex-col items-center gap-2">
            {/\.(mp4|webm|mov)(\?|$)/i.test(imagePost.image_url) ? (
              <video
                src={imagePost.image_url}
                controls
                className="rounded-lg border border-border/40 w-96 aspect-video"
              />
            ) : (
              <a href={imagePost.image_url} target="_blank" rel="noopener noreferrer" className="block" title="Click to view full size">
                <img
                  src={imagePost.image_url}
                  alt="Post graphic"
                  className="rounded-lg border border-border/40 object-contain w-96 aspect-video"
                />
              </a>
            )}
            {anyActionable && (
              <div className="flex items-center gap-2">
                {onUploadImage && (
                  <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-3 w-3 mr-1" /> Replace Image
                  </Button>
                )}
                {onGenerateImage && (
                  <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => onGenerateImage(activePost.id)}>
                    <RefreshCw className="h-3 w-3 mr-1" /> Regenerate
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Image options if no image */}
        {!imagePost.image_url && anyActionable && (
          <div className="flex items-center justify-between rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
            <div className="flex items-start gap-2 text-xs text-amber-800">
              <ImageIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{activePost.image_prompt ? `Image needed: ${activePost.image_prompt}` : 'No image attached'}</span>
            </div>
            <div className="flex items-center gap-1.5 ml-2 shrink-0">
              {onUploadImage && (
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> Upload
                </Button>
              )}
              {onGenerateImage && activePost.image_prompt && (
                <Button size="sm" variant="outline" onClick={() => onGenerateImage(activePost.id)}>
                  <ImageIcon className="h-3.5 w-3.5 mr-1" /> Generate
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Platform tabs, only if multiple platforms */}
        {!isSolo && (
          <div className="flex gap-1 border-b pb-2">
            {posts.map(p => {
              const meta = PLATFORM_META[p.platform] || PLATFORM_META.facebook;
              const Icon = meta.icon;
              const isActive = p.platform === activePlatform;
              return (
                <button
                  key={p.id}
                  onClick={() => { setActivePlatform(p.platform); setEditing(false); }}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    isActive
                      ? `${meta.color} ring-1 ring-inset ring-current/20`
                      : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {meta.label.split(', ').pop()}
                </button>
              );
            })}
          </div>
        )}

        {/* Content, shows active platform's version */}
        {editing ? (
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={5}
            className="text-sm leading-relaxed resize-y"
          />
        ) : (
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{activePost.content}</p>
        )}

        {/* Actions, approve/reject ALL posts in the group at once */}
        {anyActionable && (
          <div className="flex flex-wrap gap-2 pt-1">
            {editing ? (
              <>
                <Button
                  size="sm"
                  onClick={() => { onSaveEdit(activePost.id, draft); setEditing(false); }}
                >
                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Save This Variation
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setDraft(activePost.content); setEditing(false); }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" onClick={() => onApproveAll(allIds)}>
                  <CheckCircle className="h-3.5 w-3.5 mr-1" />
                  {isSolo ? 'Approve' : `Approve All ${posts.length}`}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditing(true); setDraft(activePost.content); }}>
                  <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={() => onRejectAll(allIds)}>
                  <XCircle className="h-3.5 w-3.5 mr-1" />
                  {isSolo ? 'Reject' : `Reject All ${posts.length}`}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Marketing Output Card
// ---------------------------------------------------------------------------

function OutputCard({
  item,
  onApprove,
  onReject,
}: {
  item: MarketingOutput;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const AgentIcon = AGENT_ICONS[item.agent_name] || FileText;
  const agentColor = AGENT_COLORS[item.agent_name] || 'bg-gray-100 text-gray-800';
  const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.pending;

  return (
    <Card className="border border-border/60 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${agentColor}`}>
              <AgentIcon className="h-3.5 w-3.5" />
              {item.agent_name}
            </span>
            <Badge variant={statusStyle.variant}>{statusStyle.label}</Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {new Date(item.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
        </div>
        <CardTitle className="text-sm font-medium pt-1">{item.title}</CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Pipeline status for processed Alex briefs */}
        {item.metadata?.processed && item.agent_name === 'Alex' && (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-800">
              <CheckCircle className="h-3.5 w-3.5" />
              Pipeline complete
            </div>
            {item.metadata.blog_post_slug && (
              <a
                href={`/admin/content/blog/edit/${item.metadata.blog_post_slug}`}
                className="text-xs text-emerald-700 hover:underline block"
              >
                Blog draft created: {item.metadata.blog_post_slug}
              </a>
            )}
            {item.metadata.social_post_ids && item.metadata.social_post_ids.length > 0 && (
              <span className="text-xs text-emerald-700 block">
                {item.metadata.social_post_ids.length} social posts queued for review
              </span>
            )}
            {item.metadata.processed_at && (
              <span className="text-[10px] text-emerald-600 block">
                Processed {new Date(item.metadata.processed_at).toLocaleString()}
              </span>
            )}
          </div>
        )}

        {/* Error status for failed pipeline */}
        {item.metadata?.last_error && !item.metadata?.processed && item.agent_name === 'Alex' && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-red-800">
              <XCircle className="h-3.5 w-3.5" />
              Pipeline failed, approve again to retry
            </div>
            <span className="text-xs text-red-600 block mt-1">{item.metadata.last_error}</span>
          </div>
        )}

        {(() => {
          // Detect JSON social content (e.g., Casey's daily social posts) and render formatted
          try {
            const parsed = JSON.parse(item.content);
            if (typeof parsed === 'object' && !Array.isArray(parsed) && (parsed.facebook || parsed.instagram || parsed.linkedin)) {
              const platformKeys = ['linkedin', 'linkedin_personal', 'linkedin_company', 'facebook', 'instagram', 'youtube'];
              return (
                <div className="space-y-3 bg-muted/50 rounded-lg p-4">
                  {platformKeys.filter(k => parsed[k]).map(key => {
                    const meta = PLATFORM_META[key] || PLATFORM_META.facebook;
                    const PlatIcon = meta.icon;
                    return (
                      <div key={key} className="space-y-1.5">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.color}`}>
                          <PlatIcon className="h-3 w-3" />
                          {meta.label}
                        </span>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap pl-1">{parsed[key]}</p>
                      </div>
                    );
                  })}
                </div>
              );
            }
          } catch { /* not JSON, render as plain text */ }

          return (
            <>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {expanded ? item.content : item.content.slice(0, 300)}
                {item.content.length > 300 && !expanded && '...'}
              </p>
              {item.content.length > 300 && (
                <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)}>
                  <Eye className="h-3.5 w-3.5 mr-1" /> {expanded ? 'Collapse' : 'Read more'}
                </Button>
              )}
            </>
          );
        })()}

        {item.status === 'pending' && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" onClick={() => onApprove(item.id)}>
              <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onReject(item.id)}>
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
          </div>
        )}

        {/* Process Now fallback for approved Alex briefs that didn't auto-fire */}
        {item.status === 'approved' && item.agent_name === 'Alex' && !item.metadata?.processed && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={async () => {
                toast({ title: 'Processing SEO brief...', description: 'Generating blog post + social posts. This may take 30 seconds.' });
                try {
                  const res = await fetch(`${SUPABASE_URL}/functions/v1/process-approved-seo-brief`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ record: { id: item.id, agent_name: item.agent_name, title: item.title, status: item.status } }),
                  });
                  const data = await res.json();
                  if (data.success) {
                    toast({ title: 'Pipeline complete!', description: `Blog draft created + ${data.social_posts_created} social posts queued.` });
                  } else {
                    toast({ title: 'Pipeline failed', description: data.error || 'Unknown error', variant: 'destructive' });
                  }
                } catch (e: any) {
                  toast({ title: 'Error', description: e.message, variant: 'destructive' });
                }
              }}
            >
              <Send className="h-3.5 w-3.5 mr-1" /> Process Now
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function ContentReview() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const agentFilter = searchParams.get('agent'); // Deep-link from Slack: ?agent=Casey
  const [socialPosts, setSocialPosts] = useState<SocialPost[]>([]);
  const [outputs, setOutputs] = useState<MarketingOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [activeTab, setActiveTab] = useState(agentFilter ? 'pending' : 'pending');
  const [selectedAgent, setSelectedAgent] = useState<string | null>(agentFilter);

  // ── Fetch ──

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [socialRes, outputRes] = await Promise.all([
      supabase
        .from('social_content_queue')
        .select('*')
        .not('status', 'eq', 'archived')
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('marketing_outputs')
        .select('*')
        .not('status', 'in', '("archived","processed")')
        .order('created_at', { ascending: false })
        .limit(50),
    ]);

    if (socialRes.data) setSocialPosts(socialRes.data as SocialPost[]);
    if (outputRes.data) setOutputs(outputRes.data as MarketingOutput[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Actions ──

  const approveSocialBatch = async (ids: string[]) => {
    const { error } = await supabase
      .from('social_content_queue')
      .update({ status: 'approved' })
      .in('id', ids);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: `Approved ${ids.length} post${ids.length > 1 ? 's' : ''}` });
      setSocialPosts(prev => prev.map(p => ids.includes(p.id) ? { ...p, status: 'approved' } : p));
      // Auto-schedule into open calendar slots
      await autoSchedulePosts(ids);
    }
  };

  const rejectSocialBatch = async (ids: string[]) => {
    const { error } = await supabase
      .from('social_content_queue')
      .update({ status: 'rejected' })
      .in('id', ids);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: `Rejected ${ids.length} post${ids.length > 1 ? 's' : ''}` });
      setSocialPosts(prev => prev.map(p => ids.includes(p.id) ? { ...p, status: 'rejected' } : p));
    }
  };

  const saveVariation = async (id: string, content: string) => {
    const { error } = await supabase
      .from('social_content_queue')
      .update({ content })
      .eq('id', id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Variation saved' });
      setSocialPosts(prev => prev.map(p => p.id === id ? { ...p, content } : p));
    }
  };

  // ── Auto-schedule approved posts into the next open calendar slot ──

  const autoSchedulePosts = async (newlyApprovedIds: string[]) => {
    const postsToSchedule = socialPosts.filter(p => newlyApprovedIds.includes(p.id) && !p.scheduled_for);
    if (postsToSchedule.length === 0) return;

    // Query existing scheduled posts for the next 2 weeks to avoid conflicts
    const now = new Date();
    const twoWeeksOut = new Date(now);
    twoWeeksOut.setDate(twoWeeksOut.getDate() + 14);

    const { data: existing } = await supabase
      .from('social_content_queue')
      .select('platform, scheduled_for')
      .not('scheduled_for', 'is', null)
      .gte('scheduled_for', now.toISOString())
      .lte('scheduled_for', twoWeeksOut.toISOString());

    const occupied = new Set<string>();
    for (const row of (existing || []) as { platform: string; scheduled_for: string }[]) {
      if (row.scheduled_for) {
        const d = new Date(row.scheduled_for);
        occupied.add(`${row.platform}-${d.toISOString().slice(0, 10)}-${d.getHours()}`);
      }
    }

    const preferredHours = [7, 8, 9, 10, 12, 14, 17];
    const startDate = new Date(now);
    startDate.setHours(0, 0, 0, 0);
    // If past 10am, start scheduling from tomorrow
    if (now.getHours() >= 10) {
      startDate.setDate(startDate.getDate() + 1);
    }

    const updates: { id: string; scheduled_for: string }[] = [];

    for (const post of postsToSchedule) {
      let found = false;
      for (let dayOffset = 0; dayOffset < 14 && !found; dayOffset++) {
        for (const hour of preferredHours) {
          const target = new Date(startDate);
          target.setDate(target.getDate() + dayOffset);
          target.setHours(hour, 0, 0, 0);
          const key = `${post.platform}-${target.toISOString().slice(0, 10)}-${hour}`;
          if (!occupied.has(key)) {
            occupied.add(key);
            updates.push({ id: post.id, scheduled_for: target.toISOString() });
            found = true;
          }
        }
      }
    }

    // Batch update in DB
    for (const upd of updates) {
      await supabase
        .from('social_content_queue')
        .update({ scheduled_for: upd.scheduled_for } as any)
        .eq('id', upd.id);
    }

    if (updates.length > 0) {
      setSocialPosts(prev => {
        const map = new Map(updates.map(u => [u.id, u.scheduled_for]));
        return prev.map(p => map.has(p.id) ? { ...p, scheduled_for: map.get(p.id)! } : p);
      });
      toast({ title: `Auto-scheduled ${updates.length} post(s) into open calendar slots` });
    }
  };

  const approveOutput = async (id: string) => {
    const { error } = await supabase
      .from('marketing_outputs')
      .update({ status: 'approved' })
      .eq('id', id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Approved' });
      setOutputs(prev => prev.map(o => o.id === id ? { ...o, status: 'approved' } : o));
    }
  };

  const rejectOutput = async (id: string) => {
    const { error } = await supabase
      .from('marketing_outputs')
      .update({ status: 'rejected' })
      .eq('id', id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Rejected' });
      setOutputs(prev => prev.map(o => o.id === id ? { ...o, status: 'rejected' } : o));
    }
  };

  // ── Publish All Approved ──

  const publishAll = async () => {
    const approved = socialPosts.filter(p => p.status === 'approved');
    if (approved.length === 0) {
      toast({ title: 'Nothing to publish', description: 'Approve some posts first.' });
      return;
    }
    setPublishing(true);
    let successes = 0;
    let failures = 0;

    for (const item of approved) {
      try {
        let res: Response;

        if (item.platform === 'youtube') {
          // YouTube posts contain [Title] and [Description] markers, parse them
          const titleMatch = item.content.match(/\[Title\]\s*(.+)/);
          const descMatch = item.content.match(/\[Description\]\s*([\s\S]*)/);
          const title = titleMatch?.[1]?.trim() || 'SeniorThrive Video';
          const description = descMatch?.[1]?.trim() || item.content;

          res = await fetch(`${SUPABASE_URL}/functions/v1/youtube-upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              video_url: item.image_url, // video URL stored in image_url field
              title,
              description,
              tags: ['SeniorThrive', 'AgingInPlace', 'SeniorWellness'],
              privacy: 'public',
              queue_id: item.id,
            }),
          });
        } else {
          // Facebook, Instagram, LinkedIn → social-publish
          res = await fetch(`${SUPABASE_URL}/functions/v1/social-publish`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              platform: item.platform === 'linkedin_company' ? 'linkedin' : item.platform,
              message: item.content,
              image_url: item.image_url,
            }),
          });
        }

        if (res.ok) {
          const result = await res.json();
          successes++;
          // Update queue row with published status and post URL
          const postUrl = result.video_url || result.results?.[item.platform]?.url || result.results?.facebook?.url || '';
          await supabase
            .from('social_content_queue')
            .update({ status: 'published', published_at: new Date().toISOString(), post_url: postUrl } as any)
            .eq('id', item.id);
          setSocialPosts(prev => prev.map(p =>
            p.id === item.id ? { ...p, status: 'published', published_at: new Date().toISOString() } : p
          ));
        } else {
          failures++;
          const errData = await res.json().catch(() => ({}));
          console.error(`Publish failed for ${item.platform}:`, errData);
        }
      } catch (e) {
        failures++;
        console.error(`Publish error for ${item.platform}:`, e);
      }
    }

    setPublishing(false);
    toast({
      title: 'Publish complete',
      description: `${successes} published, ${failures} failed.`,
      variant: failures > 0 ? 'destructive' : 'default',
    });
  };

  // ── Generate Image for a social post ──

  const generateImage = async (postId: string) => {
    const post = socialPosts.find(p => p.id === postId);
    if (!post?.image_prompt) return;

    toast({ title: 'Generating image...', description: 'This may take 10-15 seconds.' });

    try {
      // Enhance the prompt with platform context and brand guidelines
      const platformSize = post.platform === 'instagram' ? 'square 1:1 aspect ratio' : 'landscape 16:9 aspect ratio';
      const enhancedPrompt = `A bold, modern social media graphic for ${post.platform} (${platformSize}). Use navy (#1E3A5F) background, teal (#2DD4BF) accents, coral (#FF6B6B) highlights. ${post.image_prompt}. Clean, warm, magazine-quality design. NOT abstract art. Immediately readable. Include "SeniorThrive" subtly at the bottom.`;

      // Call the image-generate Edge Function (uses Gemini API)
      const genRes = await fetch(`${SUPABASE_URL}/functions/v1/image-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: enhancedPrompt, model: 'gemini-2.5-flash-image' }),
      });
      const genData = await genRes.json();

      if (genData.error || !genData.image) {
        toast({ title: 'Image generation failed', description: genData.error || 'No image returned. Try again.', variant: 'destructive' });
        return;
      }

      // Convert base64 to blob and upload to Supabase Storage
      const byteChars = atob(genData.image);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: 'image/png' });

      const storagePath = `social/${post.platform}-${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage.from('marketing-images').upload(storagePath, blob, { contentType: 'image/png', upsert: true });

      if (uploadError) {
        toast({ title: 'Upload failed', description: uploadError.message, variant: 'destructive' });
        return;
      }

      const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/marketing-images/${storagePath}`;

      // Update the queue row with the image URL
      await supabase.from('social_content_queue').update({ image_url: publicUrl }).eq('id', postId);
      setSocialPosts(prev => prev.map(p => p.id === postId ? { ...p, image_url: publicUrl } : p));
      toast({ title: 'Image generated!', description: 'Review the image and approve when ready.' });
    } catch (e: any) {
      toast({ title: 'Error generating image', description: e.message, variant: 'destructive' });
    }
  };

  // ── Upload Image for a social post ──

  const uploadImage = async (postId: string, file: File) => {
    toast({ title: 'Uploading image...' });
    try {
      const ext = file.name.split('.').pop() || 'png';
      const storagePath = `social/upload-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('marketing-images')
        .upload(storagePath, file, { contentType: file.type, upsert: true });

      if (uploadError) {
        toast({ title: 'Upload failed', description: uploadError.message, variant: 'destructive' });
        return;
      }

      const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/marketing-images/${storagePath}`;
      await supabase.from('social_content_queue').update({ image_url: publicUrl }).eq('id', postId);
      setSocialPosts(prev => prev.map(p => p.id === postId ? { ...p, image_url: publicUrl } : p));
      toast({ title: 'Image uploaded!' });
    } catch (e: any) {
      toast({ title: 'Upload error', description: e.message, variant: 'destructive' });
    }
  };

  // ── Generate All Missing Images ──

  const generateAllImages = async () => {
    const postsNeedingImages = socialPosts.filter(p => p.image_prompt && !p.image_url && ['pending', 'approved'].includes(p.status));
    if (postsNeedingImages.length === 0) {
      toast({ title: 'No images needed', description: 'All posts already have images.' });
      return;
    }
    toast({ title: `Generating ${postsNeedingImages.length} images...`, description: 'This may take a minute.' });
    let success = 0;
    for (const post of postsNeedingImages) {
      await generateImage(post.id);
      success++;
    }
    toast({ title: `Done!`, description: `${success} images generated.` });
  };

  // ── Derived data ──

  // Agent filter, from URL param or manual selection
  const uniqueAgents = useMemo(() => {
    const names = new Set<string>();
    outputs.forEach(o => names.add(o.agent_name));
    return Array.from(names).sort();
  }, [outputs]);

  const filteredSocial = useMemo(() => {
    if (!selectedAgent) return socialPosts;
    // Match social posts via their linked marketing_output
    const agentOutputIds = new Set(outputs.filter(o => o.agent_name === selectedAgent).map(o => o.id));
    return socialPosts.filter(p => p.marketing_output_id && agentOutputIds.has(p.marketing_output_id));
  }, [socialPosts, outputs, selectedAgent]);

  const filteredOutputs = useMemo(() => {
    if (!selectedAgent) return outputs;
    return outputs.filter(o => o.agent_name === selectedAgent);
  }, [outputs, selectedAgent]);

  const pendingSocial = filteredSocial.filter(p =>
    !['published', 'rejected', 'approved'].includes(p.status)
  );
  const pendingOutputs = filteredOutputs.filter(o => o.status === 'pending');
  const publishedToday = filteredSocial.filter(p => {
    if (!p.published_at) return false;
    const d = new Date(p.published_at);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  });
  const totalPending = pendingSocial.length + pendingOutputs.length;
  const approvedCount = filteredSocial.filter(p => p.status === 'approved').length;

  // ── Render ──

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <SEO title="Content Review | Admin" robots="noindex,nofollow" />

      <div className="space-y-6 max-w-5xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Content Review</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {totalPending} item{totalPending !== 1 ? 's' : ''} awaiting review
              {approvedCount > 0 && ` \u00b7 ${approvedCount} approved, ready to publish`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={generateAllImages}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            >
              <ImageIcon className="h-3.5 w-3.5 mr-1" />
              Generate Images
            </Button>
            <Button
              size="sm"
              disabled={publishing || approvedCount === 0}
              onClick={publishAll}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {publishing ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5 mr-1" />
              )}
              Publish All ({approvedCount})
            </Button>
          </div>
        </div>

        {/* Agent filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground mr-1">Filter:</span>
          <Button
            variant={selectedAgent === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedAgent(null)}
          >
            All
          </Button>
          {uniqueAgents.map(name => {
            const emoji = name === 'Dana' ? '📊' : name === 'Alex' ? '🔍' : name === 'Casey' ? '✏️' : name === 'Riley' ? '📧' : name === 'Pat' ? '🎯' : '📝';
            return (
              <Button
                key={name}
                variant={selectedAgent === name ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedAgent(selectedAgent === name ? null : name)}
              >
                {emoji} {name}
              </Button>
            );
          })}
        </div>


        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending">
              All Pending ({totalPending})
            </TabsTrigger>
            <TabsTrigger value="social">
              Social Posts ({pendingSocial.length})
            </TabsTrigger>
            <TabsTrigger value="reports">
              Reports & Briefs ({pendingOutputs.length})
            </TabsTrigger>
            <TabsTrigger value="published">
              Published Today ({publishedToday.length})
            </TabsTrigger>
          </TabsList>

          {/* ── All Pending ── */}
          <TabsContent value="pending" className="space-y-8 mt-4">
            {totalPending === 0 ? (
              <EmptyState message="All caught up. No items need review." />
            ) : (
              <>
                {pendingSocial.length > 0 && (
                  <DateGroupedSocial
                    posts={pendingSocial}
                    onApproveAll={approveSocialBatch}
                    onRejectAll={rejectSocialBatch}
                    onSaveEdit={saveVariation}
                    onGenerateImage={generateImage}
                    onUploadImage={uploadImage}
                  />
                )}
                {pendingOutputs.length > 0 && (
                  <section className="space-y-4">
                    <h2 className="text-lg font-medium">Reports & Briefs</h2>
                    {pendingOutputs.map(item => (
                      <OutputCard
                        key={item.id}
                        item={item}
                        onApprove={approveOutput}
                        onReject={rejectOutput}
                      />
                    ))}
                  </section>
                )}
              </>
            )}
          </TabsContent>

          {/* ── Social Posts ── */}
          <TabsContent value="social" className="space-y-8 mt-4">
            {pendingSocial.length === 0 ? (
              <EmptyState message="No social posts awaiting review." />
            ) : (
              <DateGroupedSocial
                posts={pendingSocial}
                onApproveAll={approveSocialBatch}
                onRejectAll={rejectSocialBatch}
                onSaveEdit={saveVariation}
                onGenerateImage={generateImage}
                onUploadImage={uploadImage}
              />
            )}
          </TabsContent>

          {/* ── Reports & Briefs ── */}
          <TabsContent value="reports" className="space-y-4 mt-4">
            {pendingOutputs.length === 0 ? (
              <EmptyState message="No reports or briefs awaiting review." />
            ) : (
              pendingOutputs.map(item => (
                <OutputCard
                  key={item.id}
                  item={item}
                  onApprove={approveOutput}
                  onReject={rejectOutput}
                />
              ))
            )}
          </TabsContent>

          {/* ── Published Today ── */}
          <TabsContent value="published" className="space-y-4 mt-4">
            {publishedToday.length === 0 ? (
              <EmptyState message="Nothing published today yet." />
            ) : (
              (() => {
                const groups = groupIntoTopics(publishedToday);
                return groups.map(group => (
                  <GroupedPostCard
                    key={group.map(p => p.id).join('-')}
                    posts={group}
                    onApproveAll={approveSocialBatch}
                    onRejectAll={rejectSocialBatch}
                    onSaveEdit={saveVariation}
                    onUploadImage={uploadImage}
                  />
                ));
              })()
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DateGroupedSocial({
  posts,
  onApproveAll,
  onRejectAll,
  onSaveEdit,
  onGenerateImage,
  onUploadImage,
}: {
  posts: SocialPost[];
  onApproveAll: (ids: string[]) => void;
  onRejectAll: (ids: string[]) => void;
  onSaveEdit: (id: string, content: string) => void;
  onGenerateImage?: (id: string) => void;
  onUploadImage?: (id: string, file: File) => void;
}) {
  const byDate = groupByDate(posts);

  return (
    <>
      {Object.entries(byDate).map(([date, datePosts]) => {
        const topicGroups = groupIntoTopics(datePosts);
        return (
          <section key={date} className="space-y-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              {date}
            </h2>
            <div className="space-y-3 pl-2">
              {topicGroups.map(group => (
                <GroupedPostCard
                  key={group.map(p => p.id).join('-')}
                  posts={group}
                  onApproveAll={onApproveAll}
                  onRejectAll={onRejectAll}
                  onSaveEdit={onSaveEdit}
                  onGenerateImage={onGenerateImage}
                  onUploadImage={onUploadImage}
                />
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <CheckCircle className="h-10 w-10 text-emerald-400 mb-3" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
